import React from 'react';
import classNames from 'classnames';
import Theme from "./Theme.js";
import { jsx as _jsx } from "react/jsx-runtime";
const EMPTY = {};
/*
  props:
  {
    unnamedTarget : 'name of layout in options | null | <false>',
    options : { // layoutOptions names
      <optionsName> : {
        layout : true | false,
        parent : null | "string parent name"
      }, ... 
  }
*/

export default class Layout extends React.Component {
  static contextType = Theme.Context;
  render() {
    const theme = this.context;
    const {
      className,
      variant,
      children,
      ...rest
    } = this.props;
    const cn = theme.classes('Layout', 'layout', variant, className);
    return /*#__PURE__*/_jsx("div", {
      className: cn,
      ...rest,
      children: renderLayout(theme, variant, children)
    });
  }
}
class Options extends React.Component {
  static contextType = Theme.Context;
  render() {
    const theme = this.context;
    const {
      name,
      children,
      style,
      className
    } = this.props;
    return renderOptions(theme, name, className, style, children);
  }
}
function renderOptions(theme, name, className, style = EMPTY, children) {
  const cn = theme.subclasses('Layout', name, className);
  const mChildren = React.Children.map(children, (child, index) => {
    const cProps = child.props || EMPTY;
    const {
      style: cStyle = EMPTY,
      className: cClassName
    } = cProps;
    return /*#__PURE__*/React.cloneElement(child, {
      style: {
        ...cStyle,
        ...style
      },
      className: classNames(cn, cClassName)
    });
  });
  return mChildren;
}

/**
 * Renders the layouts
 * @param {Theme} theme 
 * @param {string} layout the layout name
 * @param {array} children the react children to place
 */
function renderLayout(theme, layout, children) {
  if (layout == null || layout.startsWith('flex:')) {
    return children;
  }
  const result = [];
  const parents = {};
  const props = theme.props('Layout', layout);
  React.Children.forEach(children, (child, index) => {
    if (child.props == null) {
      result.push(child);
      return;
    }
    // eslint-disable-next-line prefer-const
    let {
      layoutOptions,
      className
    } = child.props;
    if (layoutOptions == null && props.unnamedTarget) {
      layoutOptions = props.unnamedTarget;
    }
    if (typeof layoutOptions === 'string') {
      // do the same job as the Options
      const cn = theme.subclasses('Layout', layoutOptions, className);
      const opts = props && props.options;
      const c = /*#__PURE__*/React.cloneElement(child, {
        className: classNames(cn, className)
      });
      ensureHierarchy(theme, result, parents, opts, layoutOptions).push(c);
    } else if (props.unnamedTarget !== false) {
      result.push(child);
    }
  });
  return result.map(createSublayouts);
}

/**
 * Makes sure that the parent layouts have been created and are in the
 * layout children arrays or the top level results array
 * @param {Theme} theme the theme, used to get the layout's classname
 * @param {Array} results the top level results array
 * @param {Object} parents the top level parent layout lookup object
 * @param {Object} options the options for the particular layout
 * @param {string} name the name of the element we need to place.
 */
function ensureHierarchy(theme, results, parents, options, name) {
  if (name == null || options == null || options[name] == null) {
    return results;
  }
  let last = null;
  const curr = options[name];
  const base = curr.layout ? name : curr.parent;
  let currParent = base;
  while (currParent != null && parents[currParent] == null) {
    // eslint-disable-next-line no-param-reassign
    parents[currParent] = {
      type: 'sublayout',
      key: currParent,
      className: theme.subclasses('Layout', currParent),
      children: []
    };
    if (last) {
      parents[currParent].children.push(parents[last]);
    }
    last = currParent;
    currParent = options[currParent] && options[currParent].parent;
    if (currParent == null) {
      // add to root
      results.push(parents[last]);
    }
  }
  return parents[base].children;
}

/**
 * Creates React elements from the sublayout object
 * @param {Object} elem the sublayout object created in ensureHistory
 */
function createSublayouts(elem) {
  if (typeof elem === 'object' && elem.type === 'sublayout') {
    return /*#__PURE__*/_jsx("div", {
      className: elem.className,
      children: elem.children.map(createSublayouts)
    }, elem.path);
  }
  return elem;
}
function classes(theme, layoutVariant) {
  if (layoutVariant == null) {
    return null;
  }
  if (typeof layoutVariant === 'string') {
    if (layoutVariant.startsWith('flex:')) {
      return theme.classes('Flex', 'flex', layoutVariant.substring(5));
    }
  }
  return theme.classes('Layout', 'layout', layoutVariant);
}
Layout.Options = Options;
Layout.classes = classes;
Layout.renderLayout = renderLayout;
Layout.renderOptions = renderOptions;

/*
  <Pane layout=""/>
    <Options name="title">
      <Text>Mr Squeekers</Text>
    </Options>
  </Pane>
  <Pane layout=""/>
    <Text layoutOptions="title">Mr Squeekers</Text>
  </Pane>
*/